import React, { useCallback, useEffect, useState } from 'react';

import Spinner from '@atlaskit/spinner';

import { trackEvent } from 'src/lib/analytics';
import jira from 'src/lib/api/jira';

import ErrorMessage from 'src/views/components/ErrorMessage';
import Toggle from 'src/views/components/Toggle';

const ShowSidebarToggle = () => {
  const [{ loading, submitting, error, hideSidebar }, setState] = useState({ loading: true });

  useEffect(() => {
    jira
      .getContext()
      .then(({ projectKey }) => Promise.all([
        jira.getProjectProperty(projectKey, 'pagerduty'),
        jira.getSettings(['sidebarVisibleByDefault'])
      ]))
      .then(values => {
        // note that project properties are typed, but setting values are always strings
        const hideInProject = values[0]?.hideSidebar;
        const visibleByDefault = values[1]?.settings?.sidebarVisibleByDefault?.value;
        if (visibleByDefault === undefined) {
          // if 'visibleByDefault' is undefined, it means that the plugin does not support the new property
          // or that the client upgraded from an installation without that property; in either case we want
          // to keep the old behaviour, i.e. only hide the sidebar if the project property is defined and set to true
          setState({ hideSidebar: hideInProject === true });
        } else {
          // if 'visibleByDefault' is defined, it means that the client is using the new visibility logic,
          // i.e. give priority to the project property if it is defined, otherwise respect the global default visibility
          setState({ hideSidebar: hideInProject === undefined ? visibleByDefault !== 'true' : hideInProject === true });
        }
      })
      .catch(error => setState({ error }));
  }, []);

  const toggle = useCallback(async () => {
    setState({ submitting: true });
    try {
      const { projectKey } = await jira.getContext();
      let val = await jira.getProjectProperty(projectKey, 'pagerduty');
      if (!val) val = {};
      val.hideSidebar = !hideSidebar;
      await jira.setProjectProperty(projectKey, 'pagerduty', val);
      setState({ hideSidebar: !hideSidebar });
      trackEvent('sidebar_project_visibility_toggle', { action: hideSidebar ? 'enable' : 'disable' });
    } catch (error) {
      setState({ error });
    }
  }, [hideSidebar]);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <span> Show PagerDuty Issue sidebar for this project</span>
        {loading && <Spinner/>}
        {!loading && <Toggle isError={Boolean(error)} isDisabled={submitting} isChecked={!hideSidebar} onChange={toggle}/>}
      </div>
      {error && <ErrorMessage error={['Can not update project property', error]}/>}
    </div>
  );
};

export default ShowSidebarToggle;
